import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { SheetHeader, SheetTitle, SheetActions } from "@/components/ui/sheet";
import { cn } from "@/utils/utils";
import { FullPageLoader } from "@common/components/Loading/FullPageLoading";
import {
  useExpenseReportsByViewQuery,
  ExpenseReportStatus,
} from "@common/utils/graphql";
import { Body } from "@hypercard/web-components";
import { Label } from "@radix-ui/react-label";
import { useMemo, useState } from "react";

interface Props {
  onSubmit: (reportId: string) => void;
  createNewReport: () => void;
}

const NewReportItem = {
  id: "new",
  title: "Create a new report",
  notes: "A unique report will be generated.",
};

export const SelectExistingReportContent = ({
  onSubmit,
  createNewReport,
}: Props) => {
  const [selectedReportId, setSelectedReportId] = useState<string>("new");
  const isNewReportSelected = selectedReportId === "new";

  const { loading: loadingReports, data: reportsData } =
    useExpenseReportsByViewQuery({
      variables: {
        offset: 0,
        pageSize: 30,
        expenseReportStatus: ExpenseReportStatus.Draft,
      },
      fetchPolicy: "cache-and-network",
    });

  const options = useMemo(() => {
    if (
      reportsData?.expenseReportsByView?.__typename !==
      "QueryExpenseReportsByViewSuccess"
    ) {
      return [];
    }
    return reportsData.expenseReportsByView?.data?.reports;
  }, [reportsData]);

  const handleSubmit = () => {
    if (isNewReportSelected) {
      createNewReport();
    } else {
      onSubmit(selectedReportId);
    }
  };

  const selectionButtons = (
    <RadioGroup defaultValue="new">
      {[NewReportItem, ...options].map((option, i) => {
        return (
          <div
            key={option.id.toString()}
            className={cn(
              "mb-2 flex items-center justify-between space-x-2 pt-4",
              { "border-t": i > 0 },
            )}
            onClick={() => setSelectedReportId(option.id.toString())}
          >
            <Label htmlFor={option.id.toString()} className="w-full">
              <Body size="md" weight="medium">
                {option.title}
              </Body>
              <Body size="sm" weight="regular" className="text-white/50">
                {option.notes || "No notes"}
              </Body>
            </Label>
            <RadioGroupItem
              value={option.id.toString()}
              id={option.id.toString()}
            />
          </div>
        );
      })}
    </RadioGroup>
  );

  const actions = (
    <SheetActions>
      <Button className="w-full" onClick={handleSubmit}>
        {isNewReportSelected ? "Create new report" : "Add to report"}
      </Button>
    </SheetActions>
  );

  return (
    <>
      <SheetHeader>
        <SheetTitle>Add Expenses to Report</SheetTitle>
        <Body size="md" weight="medium" className="mt-2 text-white/70">
          Create new report or add to existing
        </Body>
      </SheetHeader>
      {loadingReports && options.length === 0 ? (
        <FullPageLoader />
      ) : (
        <div className="flex grow flex-col overflow-auto px-sheet-gap scrollbar-thin">
          {selectionButtons}
          {actions}
        </div>
      )}
    </>
  );
};
